import type { FunctionComponent, HTMLAttributes, ReactNode } from 'react';

import { useModifiers } from '../../../styles/hooks/useModifiers';

import styles from './Badge.module.css';

export enum BadgeSize {
  Dot = 'dot',
  Tiny = 'tiny',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export interface BadgeProps extends HTMLAttributes<HTMLDivElement> {
  /** Content to display in the badge, takes priority over the value prop */
  children?: ReactNode;
  /** Value to display in the badge */
  value?: ReactNode;
  /** Size of the badge, 'dot' and 'tiny' do not display the value */
  size: BadgeSize;
  /** Whether the badge should have an impact effect (red color) */
  impact?: boolean;
  /** direction in which badge will grow, `center` by default */
  grow?: 'center' | 'start' | 'end';
  /** Additional class names to apply to the badge, for positioning purposes */
  className?: string;
}

/**
 * Component to be used for displaying a badge in other controls.
 * */
export const Badge: FunctionComponent<BadgeProps> = ({
  children,
  value,
  impact,
  size,
  className,
  grow = 'center',
  ...rest
}) => {
  const badgeWrapperWithMods = useModifiers(
    styles,
    'badge__wrapper',
    {
      [size]: true,
      [grow]: true,
    },
    { withBaseClass: true }
  );

  const badgeWithMods = useModifiers(
    styles,
    'badge',
    {
      impact,
      elevated: size === BadgeSize.Small || size === BadgeSize.Medium || size === BadgeSize.Large,
    },
    { withBaseClass: true }
  );

  return (
    <span className={`${badgeWrapperWithMods} ${className}`} {...rest}>
      <span data-testid="badge-content" className={badgeWithMods}>
        {size !== BadgeSize.Dot && size !== BadgeSize.Tiny ? children || value : null}
      </span>
    </span>
  );
};
